// WhatsApp Inbox — conversation list · live thread · contact details.
// Built on the existing whatsapp_conversations / whatsapp_messages endpoints.
// Live updates by polling (list every 10s via ?since=, open thread every 5s
// via ?after=), paused while the tab is hidden. Sending is Meta Cloud API
// only; conversations on a legacy third-party number are read-only history.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { format, isToday, isYesterday, isSameDay, formatDistanceToNowStrict } from "date-fns";
import {
  Search, Plus, Send, Check, CheckCheck, Clock, AlertTriangle, ArrowLeft, Tag, X,
  ExternalLink, UserPlus, Loader2, FileText, Paperclip, MessageCircle, RefreshCw,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { whatsappApi } from "@/services/whatsappApi";

const LIST_POLL_MS = 10_000;
const THREAD_POLL_MS = 5_000;
const WINDOW_MS = 24 * 60 * 60 * 1000;
const PAGE = 50;

const isMetaIntegration = (i) => !!i && ["meta_cloud", "api", "cloud_api", "meta"].includes((i.connection_type || "").toLowerCase());
const TYPE_META = {
  lead: { label: "Lead", cls: "bg-indigo-50 text-indigo-700 ring-indigo-200", dot: "bg-indigo-500" },
  contact: { label: "Contact", cls: "bg-sky-50 text-sky-700 ring-sky-200", dot: "bg-sky-500" },
  client: { label: "Client", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", dot: "bg-emerald-500" },
  unknown: { label: "New", cls: "bg-slate-100 text-slate-600 ring-slate-200", dot: "bg-slate-400" },
};
const FILTERS = [
  { id: "all", label: "All" }, { id: "unread", label: "Unread" }, { id: "mine", label: "Mine" },
  { id: "lead", label: "Leads" }, { id: "contact", label: "Contacts" }, { id: "client", label: "Clients" },
];

const digits = (p) => String(p || "").replace(/[^0-9]/g, "");
const fmtPhone = (p) => (digits(p) ? `+${digits(p)}` : "");
const toDate = (v) => { const d = v ? new Date(v) : null; return d && !isNaN(d.getTime()) ? d : null; };
const listTime = (iso) => { const d = toDate(iso); if (!d) return ""; if (isToday(d)) return format(d, "h:mm a"); if (isYesterday(d)) return "Yesterday"; return format(d, "d MMM"); };
const dayLabel = (d) => (isToday(d) ? "Today" : isYesterday(d) ? "Yesterday" : format(d, "EEEE, d MMM yyyy"));
const initials = (name, phone) => {
  const n = (name || "").trim();
  if (n && !/^\+?\d[\d\s-]*$/.test(n)) return n.split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return digits(phone).slice(-2) || "?";
};
const msgTs = (m) => toDate(m.timestamp || m.created_at);
const byLastDesc = (a, b) => (toDate(b.last_message_time || b.updated_at)?.getTime() || 0) - (toDate(a.last_message_time || a.updated_at)?.getTime() || 0);

function Avatar({ name, phone, size = "md" }) {
  const cls = size === "lg" ? "h-12 w-12 text-base" : "h-10 w-10 text-sm";
  return (
    <span className={`flex shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-100 to-teal-100 font-semibold text-emerald-800 ${cls}`}>
      {initials(name, phone)}
    </span>
  );
}

function TypeBadge({ type }) {
  const m = TYPE_META[type] || TYPE_META.unknown;
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${m.cls}`}>{m.label}</span>;
}

function StatusTicks({ status }) {
  if (status === "read") return <CheckCheck className="h-3.5 w-3.5 text-sky-500" title="Read" />;
  if (status === "delivered") return <CheckCheck className="h-3.5 w-3.5" title="Delivered" />;
  if (status === "sent") return <Check className="h-3.5 w-3.5" title="Sent" />;
  if (status === "failed") return <AlertTriangle className="h-3.5 w-3.5 text-rose-500" title="Failed" />;
  return <Clock className="h-3.5 w-3.5" title={status || "Pending"} />;
}

function MediaBlock({ type, src, text }) {
  const t = (type || "").toLowerCase();
  if (t === "image" || t === "sticker") {
    return <a href={src} target="_blank" rel="noreferrer"><img src={src} alt={text || "image"} loading="lazy" className="mb-1 max-h-72 max-w-full rounded-lg object-cover" /></a>;
  }
  if (t === "video") return <video controls src={src} className="mb-1 max-h-72 max-w-full rounded-lg" />;
  if (t === "audio") return <audio controls src={src} className="mb-1 w-64 max-w-full" />;
  return (
    <a href={src} target="_blank" rel="noreferrer" className="mb-1 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100">
      <FileText className="h-4 w-4" /> Open {t || "file"}
    </a>
  );
}

function MessageBubble({ m, conversationId }) {
  const out = m.direction === "outbound";
  const type = (m.type || "text").toLowerCase();
  const hasMedia = ["image", "video", "audio", "document", "sticker"].includes(type) && !!m.media_url;
  const src = hasMedia ? (/^https?:\/\//i.test(m.media_url) ? m.media_url : whatsappApi.mediaUrl(m.media_url, conversationId)) : null;
  const text = m.text && m.text !== m.media_url ? m.text : "";
  const d = msgTs(m);
  return (
    <div className={`flex ${out ? "justify-end" : "justify-start"}`} data-testid={`wa-msg-${m.message_id}`}>
      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${out ? "rounded-br-md bg-[#d9fdd3] text-slate-900" : "rounded-bl-md border border-slate-200 bg-white text-slate-900"}`}>
        {m.is_auto_reply && <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700/70">Auto-reply</div>}
        {hasMedia && <MediaBlock type={type} src={src} text={text} />}
        {text ? <div className="whitespace-pre-wrap break-words leading-relaxed">{text}</div> : (!hasMedia && <div className="italic text-slate-400">[{type}]</div>)}
        <div className="mt-1 flex items-center justify-end gap-1 text-[10px] text-slate-500">
          {out && m.sent_by_name && !m.is_auto_reply && <span className="mr-1 max-w-[140px] truncate">{m.sent_by_name} ·</span>}
          <span>{d ? format(d, "h:mm a") : ""}</span>
          {out && <StatusTicks status={m.status} />}
        </div>
      </div>
    </div>
  );
}

export default function WhatsAppInbox({ integrations = [], currentUser, businessId, onUnreadChange }) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [integrationId, setIntegrationId] = useState("");
  const [convos, setConvos] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasEarlier, setHasEarlier] = useState(false);
  const [lastInboundAt, setLastInboundAt] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [members, setMembers] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [newChat, setNewChat] = useState({ integration_id: "", phone: "", name: "" });
  const [creatingLead, setCreatingLead] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 1024);

  const activeIdRef = useRef(null);
  const sinceRef = useRef(null);
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const nearBottomRef = useRef(true);
  activeIdRef.current = activeId;

  const metaIntegrations = useMemo(() => integrations.filter(isMetaIntegration), [integrations]);
  const integrationById = useMemo(() => Object.fromEntries(integrations.map((i) => [i.integration_id, i])), [integrations]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  useEffect(() => { const t = setTimeout(() => setDebouncedSearch(search.trim()), 300); return () => clearTimeout(t); }, [search]);
  useEffect(() => {
    whatsappApi.teamMembers().then((d) => setMembers((Array.isArray(d) ? d : []).filter((m) => m.status === "approved"))).catch(() => {});
  }, []);

  const listParams = useCallback((extra = {}) => ({
    limit: PAGE,
    business_id: businessId || undefined,
    search: debouncedSearch || undefined,
    filter: ["all", "unread", "lead", "contact", "client"].includes(filter) ? (filter === "all" ? undefined : filter) : undefined,
    assigned_to: filter === "mine" ? currentUser?.user_id : undefined,
    integration_id: integrationId || undefined,
    ...extra,
  }), [debouncedSearch, filter, integrationId, currentUser, businessId]);

  const applyUnread = useCallback((list) => {
    const n = list.reduce((acc, c) => acc + ((c.unread_count || 0) > 0 ? 1 : 0), 0);
    onUnreadChange?.(n);
  }, [onUnreadChange]);

  // Initial / filter-change load
  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    whatsappApi.conversations(listParams())
      .then((d) => {
        if (cancelled) return;
        const list = d.conversations || [];
        setConvos(list); setTotal(d.total || 0); setHasMore(!!d.has_more);
        sinceRef.current = list[0]?.last_message_time || null;
        onUnreadChange?.(d.unread_total ?? 0);
      })
      .catch((e) => !cancelled && toast.error(e.message))
      .finally(() => !cancelled && setLoadingList(false));
    return () => { cancelled = true; };
  }, [listParams, onUnreadChange]);

  const loadMore = async () => {
    try {
      const d = await whatsappApi.conversations(listParams({ offset: convos.length }));
      setConvos((prev) => {
        const seen = new Set(prev.map((c) => c.conversation_id));
        return [...prev, ...(d.conversations || []).filter((c) => !seen.has(c.conversation_id))];
      });
      setHasMore(!!d.has_more); setTotal(d.total || total);
    } catch (e) { toast.error(e.message); }
  };

  // List poller
  useEffect(() => {
    const tick = async () => {
      if (document.hidden) return;
      try {
        const d = await whatsappApi.conversations(listParams({ since: sinceRef.current || undefined, limit: 100 }));
        const incoming = d.conversations || [];
        if (typeof d.unread_total === "number" && !sinceRef.current) onUnreadChange?.(d.unread_total);
        if (incoming.length === 0) return;
        setConvos((prev) => {
          const map = new Map(prev.map((c) => [c.conversation_id, c]));
          for (const c of incoming) {
            const merged = { ...(map.get(c.conversation_id) || {}), ...c };
            if (c.conversation_id === activeIdRef.current) merged.unread_count = 0;
            map.set(c.conversation_id, merged);
          }
          const next = [...map.values()].sort(byLastDesc);
          applyUnread(next);
          return next;
        });
        const newest = incoming.map((c) => c.last_message_time).filter(Boolean).sort().pop();
        if (newest && (!sinceRef.current || newest > sinceRef.current)) sinceRef.current = newest;
      } catch { /* silent */ }
    };
    const id = setInterval(tick, LIST_POLL_MS);
    return () => clearInterval(id);
  }, [listParams, applyUnread, onUnreadChange]);

  const scrollToBottom = (behavior = "auto") => requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior, block: "end" }));

  // Open a conversation
  const openConversation = useCallback(async (id) => {
    setActiveId(id); setMessages([]); setActive(null); setDraft(""); setLoadingThread(true);
    try {
      const [c, m] = await Promise.all([whatsappApi.conversation(id), whatsappApi.messages(id, { limit: PAGE })]);
      setActive(c.conversation); setMessages(m.messages || []); setHasEarlier(!!m.has_more); setLastInboundAt(m.last_inbound_at || null);
      scrollToBottom();
      whatsappApi.markRead(id).catch(() => {});
      setConvos((prev) => { const next = prev.map((x) => (x.conversation_id === id ? { ...x, unread_count: 0 } : x)); applyUnread(next); return next; });
    } catch (e) { toast.error(e.message); }
    finally { setLoadingThread(false); }
  }, [applyUnread]);

  // Thread poller
  useEffect(() => {
    if (!activeId) return undefined;
    const tick = async () => {
      if (document.hidden || !activeIdRef.current) return;
      const last = messages[messages.length - 1];
      const after = last ? (last.timestamp || last.created_at) : undefined;
      try {
        const d = await whatsappApi.messages(activeIdRef.current, after ? { after } : { limit: PAGE });
        const fresh = d.messages || [];
        if (d.last_inbound_at) setLastInboundAt(d.last_inbound_at);
        if (fresh.length === 0) return;
        setMessages((prev) => {
          const seen = new Set(prev.map((x) => x.message_id));
          const add = fresh.filter((x) => !seen.has(x.message_id));
          if (add.length === 0) return prev;
          if (add.some((x) => x.direction === "inbound")) whatsappApi.markRead(activeIdRef.current).catch(() => {});
          if (nearBottomRef.current) scrollToBottom("smooth");
          return [...prev, ...add];
        });
      } catch { /* silent */ }
    };
    const id = setInterval(tick, THREAD_POLL_MS);
    return () => clearInterval(id);
  }, [activeId, messages]);

  const onThreadScroll = () => {
    const el = scrollRef.current; if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };

  const loadEarlier = async () => {
    const first = messages[0]; if (!first || !activeId) return;
    const el = scrollRef.current; const prevHeight = el?.scrollHeight || 0;
    try {
      const d = await whatsappApi.messages(activeId, { before: first.timestamp || first.created_at, limit: PAGE });
      setMessages((prev) => [...(d.messages || []), ...prev]); setHasEarlier(!!d.has_more);
      requestAnimationFrame(() => { if (el) el.scrollTop = el.scrollHeight - prevHeight; });
    } catch (e) { toast.error(e.message); }
  };

  const activeIntegration = active ? integrationById[active.integration_id] : null;
  const legacy = !!(activeIntegration && !isMetaIntegration(activeIntegration));
  const windowOpen = lastInboundAt ? Date.now() - new Date(lastInboundAt).getTime() < WINDOW_MS : false;

  const handleSend = async () => {
    const text = draft.trim(); if (!text || !activeId || sending || legacy) return;
    setSending(true);
    try {
      const d = await whatsappApi.send(activeId, { type: "text", text });
      if (d.message) setMessages((prev) => [...prev, d.message]);
      setDraft(""); if (textareaRef.current) textareaRef.current.style.height = "auto";
      scrollToBottom("smooth");
      setConvos((prev) => prev.map((c) => (c.conversation_id === activeId ? { ...c, last_message: text.slice(0, 200), last_message_time: new Date().toISOString(), last_message_type: "text" } : c)).sort(byLastDesc));
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const handleAssign = async (userId) => {
    if (!active) return;
    const m = members.find((x) => x.user_id === userId);
    try {
      await whatsappApi.assign(active.conversation_id, userId || "", m?.name || "");
      setActive((a) => ({ ...a, assigned_user_id: userId || "", assigned_user_name: m?.name || "" }));
      setConvos((prev) => prev.map((c) => (c.conversation_id === active.conversation_id ? { ...c, assigned_user_id: userId || "", assigned_user_name: m?.name || "" } : c)));
      toast.success(userId ? `Assigned to ${m?.name || "user"}` : "Unassigned");
    } catch (e) { toast.error(e.message); }
  };

  const saveTags = async (tags) => {
    if (!active) return;
    try { await whatsappApi.setTags(active.conversation_id, tags); setActive((a) => ({ ...a, tags })); }
    catch (e) { toast.error(e.message); }
  };

  const handleCreateLead = async () => {
    if (!active) return;
    setCreatingLead(true);
    try {
      await whatsappApi.createLead({ name: active.contact_name && !/^\+?\d[\d\s-]*$/.test(active.contact_name) ? active.contact_name : `WhatsApp ${fmtPhone(active.phone_number)}`, phone: fmtPhone(active.phone_number), source: "WhatsApp", notes: "Created from WhatsApp inbox" });
      toast.success("Lead created");
      const c = await whatsappApi.conversation(active.conversation_id); setActive(c.conversation);
    } catch (e) { toast.error(e.message); }
    finally { setCreatingLead(false); }
  };

  const handleNewChat = async () => {
    const phone = digits(newChat.phone);
    if (!newChat.integration_id) return toast.error("Choose a WhatsApp number");
    if (phone.length < 10) return toast.error("Enter the phone with country code, e.g. 919876543210");
    try {
      const d = await whatsappApi.newConversation({ integration_id: newChat.integration_id, phone_number: phone, contact_name: newChat.name.trim() });
      setNewChatOpen(false); setNewChat({ integration_id: "", phone: "", name: "" });
      setConvos((prev) => (prev.some((c) => c.conversation_id === d.conversation.conversation_id) ? prev : [d.conversation, ...prev]));
      openConversation(d.conversation.conversation_id);
    } catch (e) { toast.error(e.message); }
  };

  const grouped = useMemo(() => {
    const out = []; let lastDay = null;
    for (const m of messages) {
      const d = msgTs(m);
      if (d && (!lastDay || !isSameDay(d, lastDay))) { out.push({ sep: dayLabel(d), key: `sep-${d.getTime()}` }); lastDay = d; }
      out.push({ m });
    }
    return out;
  }, [messages]);

  const showList = !isMobile || !activeId;
  const showThread = !isMobile || !!activeId;

  return (
    <div className="grid h-[calc(100vh-230px)] min-h-[560px] overflow-hidden rounded-2xl border border-slate-200 bg-white lg:grid-cols-[340px_minmax(0,1fr)] xl:grid-cols-[340px_minmax(0,1fr)_300px]" data-testid="wa-inbox">
      {/* ── Conversation list ── */}
      {showList && (
        <aside className="flex min-h-0 flex-col border-r border-slate-200">
          <div className="space-y-2.5 border-b border-slate-100 p-3">
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, message…" data-testid="wa-search"
                  className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 pl-8 pr-3 text-sm outline-none focus:border-emerald-400 focus:bg-white" />
              </div>
              <button type="button" onClick={() => { setNewChat((n) => ({ ...n, integration_id: metaIntegrations[0]?.integration_id || "" })); setNewChatOpen(true); }} data-testid="wa-new-chat"
                title="New chat" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40" disabled={metaIntegrations.length === 0}>
                <Plus className="h-4 w-4" />
              </button>
            </div>
            <div className="flex gap-1 overflow-x-auto pb-0.5">
              {FILTERS.map((f) => (
                <button key={f.id} type="button" onClick={() => setFilter(f.id)} data-testid={`wa-filter-${f.id}`}
                  className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium transition ${filter === f.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {f.label}
                </button>
              ))}
            </div>
            {integrations.length > 1 && (
              <select value={integrationId} onChange={(e) => setIntegrationId(e.target.value)} className="h-8 w-full rounded-lg border border-slate-200 bg-white px-2 text-xs">
                <option value="">All numbers</option>
                {integrations.map((i) => (
                  <option key={i.integration_id} value={i.integration_id}>{i.display_name || i.phone_number || i.sender_number}{isMetaIntegration(i) ? "" : " (legacy)"}</option>
                ))}
              </select>
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {loadingList ? (
              <div className="p-6 text-center text-sm text-slate-400">Loading conversations…</div>
            ) : convos.length === 0 ? (
              <div className="px-6 py-14 text-center">
                <MessageCircle className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                <p className="text-sm font-medium text-slate-700">No conversations</p>
                <p className="mt-1 text-xs text-slate-500">Incoming WhatsApp messages will appear here.</p>
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {convos.map((c) => {
                  const isActive = c.conversation_id === activeId;
                  const legacyRow = integrationById[c.integration_id] && !isMetaIntegration(integrationById[c.integration_id]);
                  return (
                    <li key={c.conversation_id}>
                      <button type="button" onClick={() => openConversation(c.conversation_id)} data-testid={`wa-convo-${c.conversation_id}`}
                        className={`flex w-full items-start gap-3 px-3 py-3 text-left transition ${isActive ? "bg-emerald-50/70" : "hover:bg-slate-50"}`}>
                        <Avatar name={c.contact_name} phone={c.phone_number} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className={`truncate text-sm ${(c.unread_count || 0) > 0 ? "font-semibold text-slate-900" : "font-medium text-slate-800"}`}>
                              {c.contact_name || fmtPhone(c.phone_number)}
                            </span>
                            <span className="shrink-0 text-[11px] text-slate-400">{listTime(c.last_message_time)}</span>
                          </div>
                          <div className="mt-0.5 flex items-center justify-between gap-2">
                            <span className={`truncate text-xs ${(c.unread_count || 0) > 0 ? "text-slate-700" : "text-slate-500"}`}>
                              {c.last_message || (c.last_message_type ? `[${c.last_message_type}]` : fmtPhone(c.phone_number))}
                            </span>
                            {(c.unread_count || 0) > 0 && (
                              <span className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-emerald-600 px-1.5 text-[10px] font-semibold text-white">{c.unread_count}</span>
                            )}
                          </div>
                          <div className="mt-1 flex items-center gap-1.5">
                            <TypeBadge type={c.contact_type} />
                            {c.assigned_user_name && <span className="truncate text-[10px] text-slate-400">· {c.assigned_user_name}</span>}
                            {legacyRow && <span className="rounded bg-amber-50 px-1 text-[10px] text-amber-700 ring-1 ring-inset ring-amber-200">legacy</span>}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {hasMore && (
              <div className="p-3">
                <button type="button" onClick={loadMore} className="w-full rounded-lg border border-slate-200 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50">
                  Load more ({convos.length} of {total})
                </button>
              </div>
            )}
          </div>
        </aside>
      )}

      {/* ── Thread ── */}
      {showThread && (
        <section className="flex min-h-0 flex-col bg-[#efeae2]">
          {!activeId ? (
            <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
              <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-emerald-600 shadow-sm"><MessageCircle className="h-7 w-7" /></span>
              <p className="text-sm font-medium text-slate-700">Select a conversation</p>
              <p className="mt-1 max-w-xs text-xs text-slate-500">Pick a chat on the left, or start a new one with the + button.</p>
            </div>
          ) : (
            <>
              <header className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3">
                {isMobile && (
                  <button type="button" onClick={() => setActiveId(null)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"><ArrowLeft className="h-4 w-4" /></button>
                )}
                {active ? (
                  <>
                    <Avatar name={active.contact_name} phone={active.phone_number} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold text-slate-900">{active.contact_name || fmtPhone(active.phone_number)}</span>
                        <TypeBadge type={active.contact_type} />
                      </div>
                      <div className="truncate text-xs text-slate-500">
                        {fmtPhone(active.phone_number)}
                        {activeIntegration && <> · via {activeIntegration.display_name || activeIntegration.phone_number || "WhatsApp"}</>}
                      </div>
                    </div>
                    <button type="button" onClick={() => openConversation(active.conversation_id)} title="Refresh" className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><RefreshCw className="h-4 w-4" /></button>
                  </>
                ) : <div className="h-10 flex-1 animate-pulse rounded bg-slate-100" />}
              </header>

              <div ref={scrollRef} onScroll={onThreadScroll} className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-4">
                {hasEarlier && (
                  <div className="flex justify-center">
                    <button type="button" onClick={loadEarlier} className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50">Load earlier messages</button>
                  </div>
                )}
                {loadingThread && messages.length === 0 && <div className="py-10 text-center text-xs text-slate-500">Loading messages…</div>}
                {!loadingThread && messages.length === 0 && <div className="py-10 text-center text-xs text-slate-500">No messages yet — say hello.</div>}
                {grouped.map((g) => g.sep ? (
                  <div key={g.key} className="flex justify-center py-1">
                    <span className="rounded-full bg-white/90 px-3 py-0.5 text-[11px] font-medium text-slate-500 shadow-sm">{g.sep}</span>
                  </div>
                ) : <MessageBubble key={g.m.message_id || g.m._id} m={g.m} conversationId={activeId} />)}
                <div ref={bottomRef} />
              </div>

              {/* Composer */}
              <div className="border-t border-slate-200 bg-white p-3">
                {legacy ? (
                  <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>This number is connected through a legacy third-party provider, so this conversation is read-only history. Connect the number directly with Meta in <Link to="/whatsapp/settings" className="font-semibold underline">Settings</Link> to reply.</span>
                  </div>
                ) : (
                  <>
                    {active && !windowOpen && (
                      <div className="mb-2 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                        <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                        <span>
                          {lastInboundAt ? <>The customer last wrote {formatDistanceToNowStrict(new Date(lastInboundAt), { addSuffix: true })}. </> : <>The customer hasn't written yet. </>}
                          WhatsApp only allows free-text replies within 24 hours of their last message — outside that window you need an approved <span className="font-semibold">template</span> (coming with Phase 2).
                        </span>
                      </div>
                    )}
                    <div className="flex items-end gap-2">
                      <button type="button" disabled title="Attachments arrive in Phase 2" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-slate-300"><Paperclip className="h-4 w-4" /></button>
                      <textarea
                        ref={textareaRef}
                        value={draft}
                        onChange={(e) => { setDraft(e.target.value); e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 140) + "px"; }}
                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                        rows={1}
                        placeholder="Type a message… (Enter to send, Shift+Enter for a new line)"
                        data-testid="wa-composer"
                        className="max-h-[140px] min-h-[40px] flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm outline-none focus:border-emerald-400 focus:bg-white"
                      />
                      <Button onClick={handleSend} disabled={sending || !draft.trim()} data-testid="wa-send" className="h-10 rounded-xl bg-emerald-600 px-4 text-white hover:bg-emerald-700">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {/* ── Details ── */}
      <aside className="hidden min-h-0 flex-col overflow-y-auto border-l border-slate-200 xl:flex">
        {active ? (
          <div className="space-y-5 p-4">
            <div className="flex flex-col items-center text-center">
              <Avatar name={active.contact_name} phone={active.phone_number} size="lg" />
              <div className="mt-2 text-sm font-semibold text-slate-900">{active.contact_name || fmtPhone(active.phone_number)}</div>
              <div className="text-xs text-slate-500">{fmtPhone(active.phone_number)}</div>
              <div className="mt-2"><TypeBadge type={active.contact_type} /></div>
            </div>

            <div className="space-y-1.5 text-xs">
              {active.contact_company && <div className="flex justify-between gap-2"><span className="text-slate-400">Company</span><span className="truncate font-medium text-slate-700">{active.contact_company}</span></div>}
              {active.contact_email && <div className="flex justify-between gap-2"><span className="text-slate-400">Email</span><span className="truncate font-medium text-slate-700">{active.contact_email}</span></div>}
              {active.contact_owner && <div className="flex justify-between gap-2"><span className="text-slate-400">Owner</span><span className="truncate font-medium text-slate-700">{active.contact_owner}</span></div>}
              <div className="flex justify-between gap-2"><span className="text-slate-400">Started</span><span className="font-medium text-slate-700">{toDate(active.created_at) ? format(toDate(active.created_at), "d MMM yyyy") : "—"}</span></div>
            </div>

            <div>
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">CRM</div>
              {active.contact_type === "lead" && active.contact_id ? (
                <Link to={`/leads/${active.contact_id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"><ExternalLink className="h-3.5 w-3.5" /> Open lead</Link>
              ) : active.contact_type === "client" && active.contact_id ? (
                <Link to={`/work-hub/clients/${active.contact_id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"><ExternalLink className="h-3.5 w-3.5" /> Open client</Link>
              ) : active.contact_type === "contact" && active.contact_id ? (
                <Link to="/contacts" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"><ExternalLink className="h-3.5 w-3.5" /> Open contacts</Link>
              ) : (
                <button type="button" onClick={handleCreateLead} disabled={creatingLead} data-testid="wa-create-lead" className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                  {creatingLead ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} Create lead
                </button>
              )}
            </div>

            <div>
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Assigned to</div>
              <select value={active.assigned_user_id || ""} onChange={(e) => handleAssign(e.target.value)} data-testid="wa-assign" className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm">
                <option value="">Unassigned</option>
                {members.map((m) => <option key={m.user_id} value={m.user_id}>{m.name}</option>)}
              </select>
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400"><Tag className="h-3 w-3" /> Tags</div>
              <div className="flex flex-wrap gap-1.5">
                {(active.tags || []).map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                    {t}<button type="button" onClick={() => saveTags((active.tags || []).filter((x) => x !== t))} className="text-slate-400 hover:text-rose-600"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && tagInput.trim()) { e.preventDefault(); const t = tagInput.trim(); if (!(active.tags || []).includes(t)) saveTags([...(active.tags || []), t]); setTagInput(""); } }}
                placeholder="Add tag, press Enter" className="mt-2 h-8 w-full rounded-lg border border-slate-200 px-2 text-xs" />
            </div>
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center p-6 text-center text-xs text-slate-400">Contact details appear here.</div>
        )}
      </aside>

      {/* New chat */}
      <Dialog open={newChatOpen} onOpenChange={setNewChatOpen}>
        <DialogContent className="max-w-md rounded-2xl">
          <DialogHeader><DialogTitle>New WhatsApp chat</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">From number</label>
              <select value={newChat.integration_id} onChange={(e) => setNewChat((n) => ({ ...n, integration_id: e.target.value }))} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-2 text-sm">
                <option value="">Select…</option>
                {metaIntegrations.map((i) => <option key={i.integration_id} value={i.integration_id}>{i.display_name || i.phone_number || i.phone_number_id}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Customer phone (with country code)</label>
              <input value={newChat.phone} onChange={(e) => setNewChat((n) => ({ ...n, phone: e.target.value }))} placeholder="919876543210" data-testid="wa-new-phone" className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Name (optional)</label>
              <input value={newChat.name} onChange={(e) => setNewChat((n) => ({ ...n, name: e.target.value }))} className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm" />
            </div>
            <p className="text-[11px] text-slate-500">If this person hasn't messaged you in the last 24 hours, WhatsApp requires an approved template to start the conversation (Phase 2).</p>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setNewChatOpen(false)}>Cancel</Button>
              <Button onClick={handleNewChat} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="wa-new-submit">Start chat</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
